import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Button,
    Input,
    List,
    Card,
    Badge,
    Empty,
    message,
    Layout,
    Typography,
    Avatar,
    Space,
    Divider,
    Tooltip,
    Select,
    Tag,
    Collapse,
    Popover
} from 'antd';
import {
    UserOutlined,
    SendOutlined,
    CloseOutlined,
    LogoutOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    GlobalOutlined,
    SearchOutlined,
    DeleteOutlined,
    PushpinOutlined,
    MessageOutlined,
    ReloadOutlined,
    FilterOutlined,
    SortAscendingOutlined,
    ClockCircleOutlined,
    BarChartOutlined,
    ApiOutlined,
    CheckCircleOutlined,
    SmileOutlined
} from '@ant-design/icons';
import api from '../api';

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const AdminChat = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [activeUsers, setActiveUsers] = useState({});
    const [selectedUser, setSelectedUser] = useState(null);
    const [manualBadges, setManualBadges] = useState(() => {
        const saved = localStorage.getItem('admin_chat_pinned');
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });
    const [messagesByUser, setMessagesByUser] = useState(() => {
        const saved = localStorage.getItem('admin_chat_messages');
        return saved ? JSON.parse(saved) : {};
    });
    const [inputValue, setInputValue] = useState('');
    const [broadcastValue, setBroadcastValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [minAge, setMinAge] = useState('');
    const [maxAge, setMaxAge] = useState('');
    const [sortBy, setSortBy] = useState('last_activity');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterVersion, setFilterVersion] = useState('all');
    const [minPassed, setMinPassed] = useState('');
    const [isForcedVisible, setIsForcedVisible] = useState(() => {
        return localStorage.getItem('admin_chat_force_show') === 'true';
    });
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const broadcastInputRef = useRef(null);

    const EMOJIS = ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '🥵', '🥶', '😷', '🤒', '🤕', '🤑', '🤠', '😈', '👿', '👹', '👺', '🤡', '💩', '👻', '💀', '☠️', '👽', '👾', '🤖', '🎃', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿', '😾'];

    const ADMIN_PASSWORD = 'avtodas_admin_2026';

    const addEmoji = (emoji, isBroadcast = false) => {
        if (isBroadcast) {
            setBroadcastValue(prev => prev + emoji);
            if (broadcastInputRef.current) broadcastInputRef.current.focus();
        } else {
            setInputValue(prev => prev + emoji);
            if (inputRef.current) inputRef.current.focus();
        }
    };

    useEffect(() => {
        localStorage.setItem('admin_chat_messages', JSON.stringify(messagesByUser));
    }, [messagesByUser]);

    useEffect(() => {
        localStorage.setItem('admin_chat_pinned', JSON.stringify(Array.from(manualBadges)));
    }, [manualBadges]);

    useEffect(() => {
        localStorage.setItem('admin_chat_force_show', isForcedVisible);
    }, [isForcedVisible]);

    const syncAdminStatus = (forceShow, pins) => {
        api.API.post(api.Chat + '/heartbeat', { 
            name: 'Admin',
            force_show: forceShow !== undefined ? forceShow : isForcedVisible,
            pinned_users: Array.from(pins !== undefined ? pins : manualBadges)
        }, {
            headers: { 'X-Chat-Session-Id': 'admin' }
        });
    };

    useEffect(() => {
        if (!isAuthenticated) return;

        const userPollInterval = setInterval(() => {
            api.API.get(api.Chat + '/active-users').then(response => {
                if (response) setActiveUsers(response);
            });
        }, 5000);

        const messagePollInterval = setInterval(() => {
            api.API.get(api.Chat + '/poll', {
                headers: { 'X-Chat-Session-Id': 'admin' }
            }).then(response => {
                if (response && response.messages && response.messages.length > 0) {
                    setMessagesByUser(prev => {
                        const next = { ...prev };
                        response.messages.forEach(msg => {
                            if (!next[msg.from]) next[msg.from] = [];
                            
                            // De-duplicate using ID
                            const exists = next[msg.from].some(m => m.id === msg.id || (m.timestamp === msg.timestamp && m.text === msg.text));
                            if (!exists) {
                                next[msg.from].push(msg);
                                if (selectedUser !== msg.from) {
                                    message.info(`Նոր հաղորդագրություն ${activeUsers[msg.from]?.name || msg.from.substr(0, 6)}-ից`);
                                }
                            }
                        });
                        return next;
                    });
                }
            });
        }, 3000);

        const pollInterval = setInterval(() => {
            syncAdminStatus();
        }, 5000);

        return () => {
            clearInterval(userPollInterval);
            clearInterval(messagePollInterval);
            clearInterval(pollInterval);
        };
    }, [isAuthenticated, selectedUser, activeUsers, isForcedVisible, manualBadges]);

    useEffect(() => {
        if (!selectedUser || !isAuthenticated) return;
        
        // Fetch history for selected user to ensure we have everything
        api.API.get(api.Chat + '/history/' + selectedUser).then(response => {
            if (response && Array.from(response).length > 0) {
                setMessagesByUser(prev => {
                    const next = { ...prev };
                    const existingIds = new Set((next[selectedUser] || []).map(m => m.id || (m.timestamp + m.text)));
                    const newMsgs = response.map(m => ({
                            id: m.id,
                            from: m.from,
                            text: m.text,
                            timestamp: m.timestamp,
                            is_broadcast: !!m.is_broadcast
                        })).filter(m => !existingIds.has(m.id || (m.timestamp + m.text)));
                    
                    if (newMsgs.length > 0) {
                        next[selectedUser] = [...(next[selectedUser] || []), ...newMsgs]
                            .sort((a, b) => a.timestamp - b.timestamp);
                    }
                    return next;
                });
            }
        });
    }, [selectedUser, isAuthenticated]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messagesByUser, selectedUser]);

    const toggleManualBadge = (id) => {
        setManualBadges(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            syncAdminStatus(isForcedVisible, next); // Immediate sync
            return next;
        });
    };

    const toggleGlobalVisibility = () => {
        const nextValue = !isForcedVisible;
        setIsForcedVisible(nextValue);
        syncAdminStatus(nextValue, manualBadges); // Immediate sync
    };

    const handleLogin = () => {
        if (password === ADMIN_PASSWORD) {
            setIsAuthenticated(true);
            setManualBadges(new Set()); // Reset on new session
            message.success('Մուտքը հաջողվեց');
        } else {
            message.error('Սխալ գաղտնաբառ');
        }
    };

    const handleSend = () => {
        if (!selectedUser || !inputValue.trim()) return;
        const text = inputValue.trim();
        setInputValue('');

        api.API.post(api.Chat + '/send', {
            to_session_id: selectedUser,
            text: text
        }, {
            headers: { 'X-Chat-Session-Id': 'admin' }
        }).then((res) => {
            const myMsg = { 
                id: res?.message?.id || Math.random().toString(36).substr(2, 9), 
                from: 'admin', 
                text: text, 
                timestamp: Math.floor(Date.now() / 1000) 
            };
            setMessagesByUser(prev => {
                const next = { ...prev };
                if (!next[selectedUser]) next[selectedUser] = [];
                // Check for existence
                if (!next[selectedUser].some(m => m.id === myMsg.id)) {
                    next[selectedUser].push(myMsg);
                }
                return next;
            });
        });
    };

    const handleBroadcast = () => {
        if (!broadcastValue.trim()) return;
        const text = broadcastValue.trim();
        setBroadcastValue('');

        api.API.post(api.Chat + '/broadcast', { text: text }, {
            headers: { 'X-Chat-Session-Id': 'admin' }
        }).then(res => {
            message.success(`Հաղորդագրությունը ուղարկվեց ${res.count} օգտատիրոջ`);
            const myMsg = { from: 'admin', text: text, is_broadcast: true, timestamp: Math.floor(Date.now() / 1000) };
            setMessagesByUser(prev => {
                const next = { ...prev };
                Object.keys(activeUsers).forEach(uid => {
                    if (uid === 'admin') return;
                    if (!next[uid]) next[uid] = [];
                    next[uid].push(myMsg);
                });
                return next;
            });
        });
    };

    const handleClearHistory = () => {
        api.API.post(api.Chat + '/clear-all').then(() => {
            setMessagesByUser({});
            localStorage.removeItem('admin_chat_messages');
            message.success('Չատի պատմությունը մաքրվեց');
        });
    };

    const handleCloseChat = (uId) => {
        api.API.post(api.Chat + '/close', { target_session_id: uId }, {
            headers: { 'X-Chat-Session-Id': 'admin' }
        }).then(() => {
            setMessagesByUser(prev => {
                const next = { ...prev };
                delete next[uId];
                return next;
            });
            if (selectedUser === uId) {
                setSelectedUser(null);
            }
            message.success('Չատը ավարտվեց և մաքրվեց');
        });
    };

    const handleUpdateApp = (uId) => {
        api.API.post(api.Chat + '/trigger-update', { target_session_id: uId }, {
            headers: { 'X-Chat-Session-Id': 'admin' }
        }).then(() => {
            message.success('Թարմացման հրահանգը ուղարկվեց');
        });
    };

    const handleUpdateAll = () => {
        api.API.post(api.Chat + '/trigger-update', {}, {
            headers: { 'X-Chat-Session-Id': 'admin' }
        }).then(() => {
            message.success('Թարմացման հրահանգը ուղարկվեց բոլորին');
        });
    };

    const filteredAndSortedUserIds = useMemo(() => {
        const allUserIds = Array.from(new Set([
            ...Object.keys(activeUsers),
            ...Object.keys(messagesByUser)
        ])).filter(id => id !== 'admin');

        return allUserIds
            .filter(id => {
                const user = activeUsers[id] || {};
                
                // Name/ID Match
                const nameMatch = (user.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
                                 id.toLowerCase().includes(searchQuery.toLowerCase());
                
                // Age Match
                const age = parseInt(user.age);
                const minA = parseInt(minAge);
                const maxA = parseInt(maxAge);
                let ageMatch = true;
                if (!isNaN(minA) && (isNaN(age) || age < minA)) ageMatch = false;
                if (!isNaN(maxA) && (isNaN(age) || age > maxA)) ageMatch = false;

                // Status Match
                let statusMatch = true;
                if (filterStatus !== 'all') {
                    if (filterStatus === 'online' && user.status !== 'active') statusMatch = false;
                    if (filterStatus === 'offline' && user.status !== 'offline') statusMatch = false;
                    if (filterStatus === 'closed' && user.status !== 'closed') statusMatch = false;
                }

                // Version Match
                let versionMatch = true;
                if (filterVersion !== 'all' && user.app_version !== filterVersion) versionMatch = false;

                // Progress Match
                let progressMatch = true;
                const minP = parseInt(minPassed);
                if (!isNaN(minP) && (!user.progress || user.progress.passed < minP)) progressMatch = false;

                return nameMatch && ageMatch && statusMatch && versionMatch && progressMatch;
            })
            .sort((a, b) => {
                // Pin always comes first
                const isPinnedA = manualBadges.has(a);
                const isPinnedB = manualBadges.has(b);
                if (isPinnedA !== isPinnedB) return isPinnedA ? -1 : 1;

                const userA = activeUsers[a] || {};
                const userB = activeUsers[b] || {};
                const msgsA = messagesByUser[a] || [];
                const msgsB = messagesByUser[b] || [];

                switch (sortBy) {
                    case 'last_activity':
                        const lastA = Math.max(
                            userA.last_seen || 0,
                            [...msgsA].reverse().find(m => m.from !== 'admin')?.timestamp || 0
                        );
                        const lastB = Math.max(
                            userB.last_seen || 0,
                            [...msgsB].reverse().find(m => m.from !== 'admin')?.timestamp || 0
                        );
                        return lastB - lastA;

                    case 'progress':
                        const progA = userA.progress?.passed || 0;
                        const progB = userB.progress?.passed || 0;
                        if (progA !== progB) return progB - progA;
                        return (userA.progress?.true || 0) - (userB.progress?.true || 0);

                    case 'version':
                        return (userB.app_version || '').localeCompare(userA.app_version || '');

                    case 'name':
                        return (userA.name || 'Z').localeCompare(userB.name || 'Z');

                    case 'age':
                        return (parseInt(userB.age) || 0) - (parseInt(userA.age) || 0);

                    default:
                        return b.localeCompare(a);
                }
            });
    }, [activeUsers, searchQuery, minAge, maxAge, messagesByUser, sortBy, filterStatus, filterVersion, minPassed]);

    if (!isAuthenticated) {
        return (
            <div style={{ height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f0f2f5' }}>
                <Card title={<Title level={4} style={{margin:0}}>Ադմինիստրատորի Մուտք</Title>} style={{ width: 400, textAlign: 'center', borderRadius: 16, boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
                    <ThunderboltOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 20 }} />
                    <Input.Password 
                        placeholder="Գաղտնաբառ" 
                        value={password} 
                        onChange={e => setPassword(e.target.value)}
                        onPressEnter={handleLogin}
                        style={{ marginBottom: 20, borderRadius: 8 }}
                        size="large"
                    />
                    <Button type="primary" block onClick={handleLogin} size="large" style={{ borderRadius: 8 }}>Մուտք</Button>
                </Card>
            </div>
        );
    }

    return (
        <Layout style={{ height: '88vh', background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.05)' }}>
            <Sider width={350} theme="light" className="admin-sider" style={{ borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ padding: '20px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                        <Space align="center" size="middle">
                                                    <Title level={3} style={{ margin: 0 }}>Չատեր</Title>
                                                    <Badge count={filteredAndSortedUserIds.length} overflowCount={999} style={{ backgroundColor: '#1890ff' }} />
                                                    <Tooltip title={isForcedVisible ? "Թաքցնել չատը բոլորից" : "Ցուցադրել չատը բոլորին"}>
                                                        <Button 
                                                            type={isForcedVisible ? "primary" : "default"} 
                                                            shape="circle" 
                                                            icon={<MessageOutlined />} 
                                                            size="small"
                                                            onClick={toggleGlobalVisibility}
                                                        />
                                                    </Tooltip>
                                                </Space>
                            
                        <Space>
                            <Tooltip title="Թարմացնել բոլորի հավելվածը">
                                <Button type="text" icon={<ReloadOutlined />} onClick={handleUpdateAll} />
                            </Tooltip>
                            <Tooltip title="Մաքրել պատմությունը">
                                <Button type="text" icon={<DeleteOutlined />} onClick={handleClearHistory} />
                            </Tooltip>
                            <Tooltip title="Դուրս գալ">
                                <Button type="text" danger icon={<LogoutOutlined />} onClick={() => setIsAuthenticated(false)} />
                            </Tooltip>
                        </Space>
                    </div>

                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                        <Input 
                            placeholder="Փնտրել անուն կամ ID..." 
                            prefix={<SearchOutlined />} 
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            style={{ borderRadius: 8 }}
                            allowClear
                        />
                        
                        <Collapse ghost size="small" expandIconPosition="end" style={{ background: '#f8f9fa', borderRadius: 8, marginTop: 4 }}>
                            <Collapse.Panel header={<Space><FilterOutlined style={{ fontSize: 12 }} /> <Text style={{ fontSize: 12 }}>Ֆիլտրեր և Սորտավորում</Text></Space>} key="1">
                                <Space direction="vertical" style={{ width: '100%' }} size="small">
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Սորտավորել ըստ՝</Text>
                                        <Select 
                                            size="small"
                                            style={{ width: '100%' }} 
                                            value={sortBy} 
                                            onChange={setSortBy}
                                        >
                                            <Option value="last_activity"><ClockCircleOutlined /> Վերջին ակտիվություն</Option>
                                            <Option value="progress"><BarChartOutlined /> Առաջընթաց</Option>
                                            <Option value="version"><ApiOutlined /> Հավելվածի տարբերակ</Option>
                                            <Option value="name"><UserOutlined /> Անուն</Option>
                                            <Option value="age">Տարիք</Option>
                                        </Select>
                                    </div>

                                    <Divider style={{ margin: '4px 0' }} />

                                    <div>
                                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Կարգավիճակ</Text>
                                        <Select 
                                            size="small"
                                            style={{ width: '100%' }} 
                                            value={filterStatus} 
                                            onChange={setFilterStatus}
                                        >
                                            <Option value="all">Բոլորը</Option>
                                            <Option value="online">Միայն Օնլայն</Option>
                                            <Option value="offline">Օֆլայն</Option>
                                            <Option value="closed">Փակված</Option>
                                        </Select>
                                    </div>

                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <div style={{ flex: 1 }}>
                                            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Տարիք</Text>
                                            <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                <Input 
                                                    size="small"
                                                    type="number"
                                                    placeholder="Min" 
                                                    value={minAge}
                                                    onChange={e => setMinAge(e.target.value)}
                                                />
                                                <Input 
                                                    size="small"
                                                    type="number"
                                                    placeholder="Max" 
                                                    value={maxAge}
                                                    onChange={e => setMaxAge(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Թեստեր (Min)</Text>
                                            <Input 
                                                size="small"
                                                type="number"
                                                placeholder="Min" 
                                                value={minPassed}
                                                onChange={e => setMinPassed(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Հավելվածի տարբերակ</Text>
                                        <Select 
                                            size="small"
                                            style={{ width: '100%' }} 
                                            value={filterVersion} 
                                            onChange={setFilterVersion}
                                        >
                                            <Option value="all">Բոլորը</Option>
                                            {Array.from(new Set(Object.values(activeUsers).map(u => u.app_version).filter(Boolean))).map(v => (
                                                <Option key={v} value={v}>{v}</Option>
                                            ))}
                                        </Select>
                                    </div>
                                    
                                    <Button 
                                        size="small" 
                                        type="link" 
                                        style={{ fontSize: 11, padding: 0 }} 
                                        onClick={() => {
                                            setSearchQuery('');
                                            setMinAge('');
                                            setMaxAge('');
                                            setSortBy('last_activity');
                                            setFilterStatus('all');
                                            setFilterVersion('all');
                                            setMinPassed('');
                                        }}
                                    >
                                        Մաքրել ֆիլտրերը
                                    </Button>
                                </Space>
                            </Collapse.Panel>
                        </Collapse>
                    </Space>
                    
                    <Divider style={{ margin: '15px 0' }} />

                    <Card size="small" style={{ borderRadius: 12, background: '#f9f9f9', border: '1px dashed #d9d9d9' }}>
                        <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <GlobalOutlined style={{ color: '#1890ff' }} />
                                <Text strong style={{ fontSize: 12 }}>Հայտարարություն բոլորին</Text>
                            </div>
                            <Popover
                                content={
                                    <div style={{ width: 250, height: 150, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 4, padding: 4 }}>
                                        {EMOJIS.slice(0, 50).map(emoji => (
                                            <span 
                                                key={emoji} 
                                                style={{ fontSize: 18, cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => addEmoji(emoji, true)}
                                            >
                                                {emoji}
                                            </span>
                                        ))}
                                    </div>
                                }
                                title="Էմոջի"
                                trigger="click"
                                placement="right"
                            >
                                <Button size="small" type="text" icon={<SmileOutlined style={{ color: '#1890ff' }} />} />
                            </Popover>
                        </div>
                        <Input.TextArea 
                            ref={broadcastInputRef}
                            placeholder="Գրեք բոլորին..." 
                            rows={2} 
                            value={broadcastValue}
                            onChange={e => setBroadcastValue(e.target.value)}
                            style={{ marginBottom: 8, borderRadius: 8, fontSize: 12 }}
                        />
                        <Button 
                            type="primary" 
                            block 
                            size="small" 
                            icon={<TeamOutlined />} 
                            onClick={handleBroadcast}
                            style={{ borderRadius: 6 }}
                        >
                            Ուղարկել
                        </Button>
                    </Card>
                </div>

                <div className="user-list-container" style={{ flex: 1, overflowY: 'auto', paddingBottom: 20 }}>
                    <List
                        itemLayout="horizontal"
                        dataSource={filteredAndSortedUserIds}
                        renderItem={id => {
                            const user = activeUsers[id] || { name: 'Անհայտ օգտատեր', status: 'offline' };
                            const isOffline = user.status === 'offline' || user.status === 'closed';
                            const badgeColor = user.status === 'active' ? '#52c41a' : '#ff4d4f';
                            const isFilled = user.name && user.name !== 'Անանուն' && !user.name.startsWith('Guest') && user.name !== 'Անհայտ օգտատեր';
                            
                            return (
                                <List.Item 
                                    onClick={() => setSelectedUser(id)}
                                    style={{ 
                                        cursor: 'pointer', 
                                        padding: '12px 20px',
                                        background: selectedUser === id ? '#e6f7ff' : isFilled ? '#f9f9f9' : 'transparent',
                                        borderLeft: selectedUser === id ? '4px solid #1890ff' : '4px solid transparent',
                                        transition: 'all 0.3s',
                                        opacity: isOffline ? 0.6 : 1
                                    }}
                                    actions={[
                                        <Tooltip title="Ջնջել պատմությունը">
                                            <Button 
                                                type="text" 
                                                danger 
                                                icon={<DeleteOutlined />} 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCloseChat(id);
                                                }}
                                                size="small"
                                            />
                                        </Tooltip>
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={
                                            <Badge count={isOffline ? '!' : 0} offset={[-2, 32]} color="#ff4d4f">
                                                <Badge dot={manualBadges.has(id) || (messagesByUser[id]?.length > 0 && messagesByUser[id][messagesByUser[id].length - 1].from !== 'admin')} color="#1890ff" offset={[-2, 5]}>
                                                    <Avatar src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${id}`} style={{ border: `2px solid ${badgeColor}` }} />
                                                </Badge>
                                            </Badge>
                                        }
                                        title={
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <Text strong={selectedUser === id || isFilled} delete={user.status === 'closed'}>
                                                    {user.name || id.substr(0, 8)} {user.age && user.age !== 'N/A' && user.age !== 0 ? `(${user.age} տ․)` : ''}
                                                </Text>
                                                <Text type="secondary" style={{ fontSize: 10 }}>
                                                    {user.status === 'active' ? 'Օնլայն' : user.status === 'closed' ? 'Փակված' : 'Անհասանելի'}
                                                </Text>
                                            </div>
                                        }
                                        description={
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Text type="secondary" style={{ fontSize: 10 }}>ID: {id.substr(0, 8)}...</Text>
                                                    <Space size={4}>
                                                        {user.app_version && <Tag size="small" style={{ fontSize: 8, margin: 0 }}>v{user.app_version}</Tag>}
                                                        {user.ip && <Text type="secondary" style={{ fontSize: 10 }}>{user.ip}</Text>}
                                                    </Space>
                                                </div>
                                                {user.progress && (
                                                    <div style={{ fontSize: 10, color: '#8c8c8c' }}>
                                                        <Space split={<Divider type="vertical" style={{ margin: 0 }} />}>
                                                            <span>✅ {user.progress.true}</span>
                                                            <span>❌ {user.progress.wrong}</span>
                                                            <span>📊 {user.progress.passed}/60</span>
                                                        </Space>
                                                    </div>
                                                )}
                                                {user.current_page && (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Text type="secondary" style={{ fontSize: 9, fontStyle: 'italic' }}>
                                                            📍 {user.current_page}
                                                        </Text>
                                                        {user.push_time && (
                                                            <Text type="secondary" style={{ fontSize: 8 }}>
                                                                🚀 {user.push_time}
                                                            </Text>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                        locale={{ emptyText: <div style={{ padding: 40 }}><Empty description="Օգտատերեր չկան" /></div> }}
                    />
                </div>
            </Sider>
            
            <Layout>
                {selectedUser ? (
                    <>
                        <Header style={{ background: '#fff', padding: '0 25px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 'auto', minHeight: 80, paddingBlock: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <Badge dot color={activeUsers[selectedUser]?.status === 'active' ? '#52c41a' : '#ff4d4f'}>
                                    <Avatar size="large" src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedUser}`} />
                                </Badge>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Title level={4} style={{ margin: 0 }}>
                                            {activeUsers[selectedUser]?.name || selectedUser} 
                                            {activeUsers[selectedUser]?.age && activeUsers[selectedUser]?.age !== 'N/A' && activeUsers[selectedUser]?.age !== 0 ? ` (${activeUsers[selectedUser]?.age} տարեկան)` : ''}
                                        </Title>
                                        <Tag color={activeUsers[selectedUser]?.status === 'active' ? "success" : "error"} style={{ borderRadius: 10, margin: 0 }}>
                                            {activeUsers[selectedUser]?.status === 'active' ? 'Օնլայն' : 'Անհասանելի'}
                                        </Tag>
                                    </div>
                                    <Space size="middle" style={{ marginTop: 4 }}>
                                        {activeUsers[selectedUser]?.app_version && (
                                            <Space size={4}>
                                                <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>v{activeUsers[selectedUser].app_version}</Tag>
                                                {activeUsers[selectedUser].push_time && (
                                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                                        (Push: {activeUsers[selectedUser].push_time})
                                                    </Text>
                                                )}
                                            </Space>
                                        )}
                                        {activeUsers[selectedUser]?.ip && (
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                <GlobalOutlined /> {activeUsers[selectedUser].ip}
                                            </Text>
                                        )}
                                        {activeUsers[selectedUser]?.current_page && (
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                📍 {activeUsers[selectedUser].current_page}
                                            </Text>
                                        )}
                                    </Space>
                                    {activeUsers[selectedUser]?.progress && (
                                        <div style={{ marginTop: 4 }}>
                                            <Space split={<Divider type="vertical" />} style={{ fontSize: 12, color: '#595959' }}>
                                                <Text type="success">Ճիշտ: {activeUsers[selectedUser].progress.true}</Text>
                                                <Text type="danger">Սխալ: {activeUsers[selectedUser].progress.wrong}</Text>
                                                <Text type="warning">Թեստեր: {activeUsers[selectedUser].progress.passed}/60</Text>
                                                <Text strong style={{ color: '#1890ff' }}>
                                                    Ճշտություն: {activeUsers[selectedUser].progress.passed > 0 ? Math.round((activeUsers[selectedUser].progress.true / (activeUsers[selectedUser].progress.passed * 20)) * 100) : 0}%
                                                </Text>
                                            </Space>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <Space>
                                <Tooltip title="Թարմացնել օգտատիրոջ հավելվածը (մաքրել քեշը)">
                                    <Button 
                                        icon={<ReloadOutlined />} 
                                        onClick={() => handleUpdateApp(selectedUser)}
                                    >
                                        Թարմացնել
                                    </Button>
                                </Tooltip>
                                <Tooltip title={manualBadges.has(selectedUser) ? "Թաքցնել չատը օգտատիրոջից" : "Ցուցադրել չատը օգտատիրոջը"}>
                                    <Button 
                                        type={manualBadges.has(selectedUser) ? "primary" : "default"} 
                                        icon={<PushpinOutlined />} 
                                        onClick={() => toggleManualBadge(selectedUser)}
                                    >
                                        {manualBadges.has(selectedUser) ? "Չատը միացված է" : "Ցուցադրել չատը"}
                                    </Button>
                                </Tooltip>
                                <Button danger type="text" icon={<CloseOutlined />} onClick={() => handleCloseChat(selectedUser)}>Ավարտել Չատը</Button>
                            </Space>
                        </Header>
                        <Content style={{ padding: '25px', display: 'flex', flexDirection: 'column', background: '#f5f7f9' }}>
                            <div className="message-list-container" style={{ flex: 1, overflowY: 'auto', marginBottom: 20, paddingRight: 10 }}>
                                {messagesByUser[selectedUser]?.map((item, idx) => {
                                    const isMe = item.from === 'admin';
                                    const time = item.timestamp ? new Date(item.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                                    return (
                                        <div key={idx} style={{ 
                                            display: 'flex', 
                                            flexDirection: 'column',
                                            alignItems: isMe ? 'flex-end' : 'flex-start',
                                            marginBottom: 12
                                        }}>
                                            <div style={{ 
                                                maxWidth: '85%', 
                                                padding: '10px 16px', 
                                                borderRadius: 16, 
                                                borderTopRightRadius: isMe ? 2 : 16, 
                                                borderTopLeftRadius: isMe ? 16 : 2,
                                                background: isMe ? '#1890ff' : '#fff',
                                                color: isMe ? '#fff' : '#333',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                                                fontSize: '14px',
                                                position: 'relative'
                                            }}>
                                                {item.text}
                                                <div style={{ 
                                                    fontSize: '10px', 
                                                    marginTop: 4, 
                                                    opacity: 0.6, 
                                                    textAlign: 'right',
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                    alignItems: 'center',
                                                    gap: 4
                                                }}>
                                                    {item.is_broadcast && isMe && <span style={{ fontStyle: 'italic' }}>Համընդհանուր • </span>}
                                                    {time}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>
                            <div style={{ padding: '15px', background: '#fff', borderRadius: 16, boxShadow: '0 4px 15px rgba(0,0,0,0.05)', display: 'flex', gap: 12, alignItems: 'center' }}>
                                <Popover
                                    content={
                                        <div style={{ width: 300, height: 200, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 8, padding: 8 }}>
                                            {EMOJIS.map(emoji => (
                                                <span 
                                                    key={emoji} 
                                                    style={{ fontSize: 20, cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={() => addEmoji(emoji)}
                                                >
                                                    {emoji}
                                                </span>
                                            ))}
                                        </div>
                                    }
                                    title="Ընտրեք էմոջի"
                                    trigger="click"
                                    placement="topLeft"
                                >
                                    <Button 
                                        type="text" 
                                        icon={<SmileOutlined style={{ fontSize: 20, color: '#1890ff' }} />} 
                                        style={{ height: 40, width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    />
                                </Popover>
                                <Input 
                                    ref={inputRef}
                                    size="large"
                                    placeholder="Գրեք ձեր պատասխանը..." 
                                    value={inputValue} 
                                    onChange={e => setInputValue(e.target.value)}
                                    onPressEnter={handleSend}
                                    style={{ borderRadius: 12, border: 'none', background: '#f0f2f5', flex: 1 }}
                                    disabled={activeUsers[selectedUser]?.status === 'closed'}
                                />
                                <Button 
                                    size="large" 
                                    type="primary" 
                                    icon={<SendOutlined />} 
                                    onClick={handleSend} 
                                    style={{ borderRadius: 12, width: 60 }} 
                                    disabled={activeUsers[selectedUser]?.status === 'closed'}
                                />
                            </div>
                        </Content>
                    </>
                ) : (
                    <Content style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f5f7f9' }}>
                        <div style={{ textAlign: 'center', opacity: 0.5 }}>
                            <TeamOutlined style={{ fontSize: 64, marginBottom: 20 }} />
                            <Title level={4}>Ընտրեք օգտատիրոջ չատը սկսելու համար</Title>
                            <Text>Կամ օգտագործեք ձախ կողմի վահանակը բոլորին հայտարարություն ուղարկելու համար</Text>
                        </div>
                    </Content>
                )}
            </Layout>
            <style dangerouslySetInnerHTML={{ __html: `
                .user-list-container::-webkit-scrollbar,
                .message-list-container::-webkit-scrollbar {
                    width: 6px;
                }
                .user-list-container::-webkit-scrollbar-thumb,
                .message-list-container::-webkit-scrollbar-thumb {
                    background-color: #e8e8e8;
                    border-radius: 10px;
                }
                .user-list-container::-webkit-scrollbar-track,
                .message-list-container::-webkit-scrollbar-track {
                    background-color: transparent;
                }
                .admin-sider .ant-layout-sider-children {
                    display: flex;
                    flex-direction: column;
                }
            `}} />
        </Layout>
    );
};

export default AdminChat;
